import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IUnitType, IUnitTypeInput, unitTypesAtom, unitTypesLoadingAtom } from "./types";

type UseUnitTypes = [IUnitType[], {
    list: (query?: Record<string, any>) => Promise<IUnitType[] | null>;
    getById: (id: number) => Promise<IUnitType | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IUnitType[] | null>;
    create: (input: IUnitTypeInput) => Promise<IUnitType | null>;
    update: (id: number, input: IUnitTypeInput) => Promise<IUnitType | null>;
    remove: (id: number) => Promise<IUnitType | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useUnitTypes = (): UseUnitTypes => {
    const [items, setItems] = useAtom(unitTypesAtom);
    const setLoading = useSetAtom(unitTypesLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getUnitTypes(query: $query){ id name description created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getUnitTypes);
            return data.getUnitTypes as IUnitType[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch unit types.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getUnitTypeById(id:$id){ id name description created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getUnitTypeById as IUnitType;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedUnitTypes(first:$first,offset:$offset,query:$query){ id name description created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedUnitTypes as IUnitType[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IUnitTypeInput) => {
        try { const q = `mutation($input:UnitTypeInput!){ createUnitType(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createUnitType as IUnitType;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IUnitTypeInput) => {
        try { const q = `mutation($id:ID!,$input:UnitTypeInput!){ updateUnitType(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateUnitType as IUnitType;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteUnitType(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteUnitType as IUnitType;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


